import { useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { AmbientGlow, ArrowIcon, CheckMark, TapeLabel } from "./_shared";

// Ported from Claude Design handoff (sections-rest: CTAFinal).
// 2-column split: copy left, boxed form right with elev background.

type FieldSpec =
  | {
      id: keyof FormShape;
      label: string;
      type: "text" | "email" | "tel" | "url";
      required: boolean;
    }
  | {
      id: keyof FormShape;
      label: string;
      type: "select";
      required: boolean;
      options: string[];
    }
  | {
      id: keyof FormShape;
      label: string;
      type: "textarea";
      required: boolean;
    };

type FormShape = {
  name: string;
  email: string;
  phone: string;
  website: string;
  employees: string;
  message: string;
};

const FIELDS: FieldSpec[] = [
  { id: "name", label: "Seu nome", type: "text", required: true },
  { id: "email", label: "E-mail", type: "email", required: true },
  { id: "phone", label: "Telefone", type: "tel", required: true },
  { id: "website", label: "Site da empresa", type: "url", required: false },
  {
    id: "employees",
    label: "Nº de colaboradores",
    type: "select",
    required: true,
    options: ["1 – 10", "11 – 50", "51 – 200", "200+"],
  },
  {
    id: "message",
    label: "Conte brevemente sua necessidade",
    type: "textarea",
    required: false,
  },
];

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 14px",
  background: "var(--cy-bg)",
  border: "1px solid var(--cy-ink-10)",
  color: "var(--cy-ink-100)",
  fontSize: 14,
  fontFamily: "var(--cy-fs-sans)",
  borderRadius: 6,
  outline: "none",
  transition: "border-color .15s ease",
};

export function CTAFinal() {
  const [form, setForm] = useState<FormShape>({
    name: "",
    email: "",
    phone: "",
    website: "",
    employees: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update =
    (field: keyof FormShape) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    await new Promise((r) => setTimeout(r, 400));
    setSending(false);
    setSent(true);
  };

  return (
    <section
      id="contato"
      className="cy-section"
      style={{
        position: "relative",
        padding: "140px 40px",
        background: "var(--cy-bg)",
        borderTop: "1px solid var(--cy-ink-10)",
        overflow: "hidden",
      }}
    >
      <AmbientGlow variant="tl-br" />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <div
          className="cy-cta-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 80,
            alignItems: "start",
          }}
        >
          <div>
            <TapeLabel>VAMOS CONVERSAR</TapeLabel>
            <h2
              style={{
                fontFamily: "var(--cy-fs-sans)",
                fontSize: "clamp(36px, 5vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.035em",
                fontWeight: 500,
                margin: "20px 0 24px",
                color: "var(--cy-ink-100)",
                textWrap: "balance",
              }}
            >
              Pronto para proteger sua empresa?
            </h2>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.55,
                color: "var(--cy-ink-80)",
                maxWidth: 440,
                margin: 0,
              }}
            >
              Fale com nosso consultor e descubra como o Cyphron pode
              simplificar a segurança da sua empresa. Sem compromisso, sem
              jargão técnico.
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--cy-ink-60)",
                marginTop: 28,
              }}
            >
              Preencha o formulário ao lado e entraremos em contato em breve.
            </p>
          </div>

          <div
            style={{
              padding: 32,
              border: "1px solid var(--cy-ink-10)",
              background: "var(--cy-bg-elev)",
              borderRadius: 10,
            }}
          >
            {sent ? (
              <div style={{ padding: "40px 0", textAlign: "center" }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(132,255,0,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <CheckMark size={28} />
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 500,
                    color: "var(--cy-ink-100)",
                    marginBottom: 8,
                  }}
                >
                  Mensagem enviada!
                </div>
                <div
                  style={{ fontSize: 14, color: "var(--cy-ink-60)" }}
                >
                  Entraremos em contato em breve pelo email informado.
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="cy-tape" style={{ marginBottom: 18 }}>
                  Deixe seus dados que entraremos em contato
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                  }}
                >
                  {FIELDS.map((f) => (
                    <div key={f.id}>
                      <label
                        style={{
                          display: "block",
                          fontSize: 12,
                          color: "var(--cy-ink-60)",
                          marginBottom: 6,
                        }}
                      >
                        {f.label}
                        {f.required && (
                          <span style={{ color: "var(--cy-accent)" }}> *</span>
                        )}
                      </label>
                      {f.type === "textarea" ? (
                        <textarea
                          rows={3}
                          style={{ ...inputStyle, resize: "vertical" }}
                          value={form[f.id]}
                          onChange={update(f.id)}
                        />
                      ) : f.type === "select" ? (
                        <select
                          style={inputStyle}
                          required={f.required}
                          value={form[f.id]}
                          onChange={update(f.id)}
                        >
                          <option value="">Selecione</option>
                          {f.options.map((o) => (
                            <option
                              key={o}
                              value={o}
                              style={{ background: "#0f172a" }}
                            >
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={f.type}
                          style={inputStyle}
                          required={f.required}
                          value={form[f.id]}
                          onChange={update(f.id)}
                        />
                      )}
                    </div>
                  ))}
                  <button
                    type="submit"
                    disabled={sending}
                    className="cy-btn cy-btn--primary"
                    style={{
                      marginTop: 6,
                      justifyContent: "center",
                      opacity: sending ? 0.7 : 1,
                    }}
                  >
                    {sending ? "Enviando..." : "Enviar Mensagem"}
                    {!sending && (
                      <span className="cy-arrow">
                        <ArrowIcon size={14} color="#0A1500" />
                      </span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
